import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EditIncidentDialog from 'components/IncidentConfig/EditIncidentDialog';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { IncidentTypes } from 'components/IncidentConfig/IncidentTypes';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import {
  useCurrentPeriod,
  useAllIncidents,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';
import { useUpdateV1IncidentMutation } from 'hooks/v1Incident/useUpdateV1Incident';
import dayjs from 'dayjs';
import { updateCurrentIncident } from '../../actions/diceActions';
import { useNavigate } from 'react-router';

export default function IncidentAdminToolbar() {
  const dispatch = useDispatch();
  const [isEditIncidentOpen, setIsEditIncidentOpen] = useState(false);
  const [incidentType, setIncidentType] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);

  const { data: incidents = [] } = useAllIncidents();
  const updateV1Incident = useUpdateV1IncidentMutation();

  const { data: currentIncident } = useIncident();

  const reduxValidateRBACPermissionForActionResult = useSelector((state) => {
    return state.app.validateRBACPermissionForActionResult;
  });
  const currentPeriod = useCurrentPeriod();
  const navigate = useNavigate();

  const handleArchiveClick = () => {
    setShowConfirmDialog(true);
  };

  const archiveIncident = async () => {
    await updateV1Incident.mutateAsync({
      v1IncidentId: currentIncident.id,
      name: currentIncident.name,
      archived: 1,
    });

    dispatch(updateCurrentIncident(selectedWorkspace));
    navigate(`/iap/${selectedWorkspace.id}/settings`);
    setShowConfirmDialog(false);
  };

  useEffect(() => {
    if (!!currentIncident && currentIncident?.incident_types?.length > 0) {
      setIncidentType(
        currentIncident.incident_types.map((i) => ({
          label: i.trim(),
          value: i.trim(),
        }))
      );
    } else {
      setIncidentType([]);
    }
  }, [currentIncident]);

  const incidentTypeHandler = async (e) => {
    if (e.length <= 0) {
      return;
    }

    const updatedIncident = { ...currentIncident };
    updatedIncident.incident_types = e.map((i) => i.value.trim());

    await updateV1Incident.mutateAsync({
      v1IncidentId: currentIncident.id,
      name: currentIncident.name,
      archived: 0,
      incidentTypes: updatedIncident.incident_types,
    });
    setIncidentType(e);
  };

  if (!currentIncident && !reduxValidateRBACPermissionForActionResult) {
    return <div className="incidents-admin-toolbar">Loading Incidents...</div>;
  }

  return (
    <>
      <div className="bg-gray-800 rounded p-3 p-md-4 mb-4">
        <h6 className="text-uppercase mt-0">Incident Configuration</h6>
        <hr className="dashed my-3" />
        {(!!currentIncident && (
          <>
            <label className="form-label fw-bold">
              Currently Selected Incident
            </label>
            <div>
              <span>{currentIncident && currentIncident.name}</span>
            </div>
          </>
        )) || (
          <span className="d-block txt--sml mb-3">
            You do not have an Incident selected. You will need to select an
            Incident to use Layouts and other tools.
          </span>
        )}
        {!!currentIncident && (
          <>
            <div className="mt-3">
              <div>
                <label className="form-label fw-bold">
                  Current Operational Period
                </label>
              </div>
              <div className="mb-3">
                Operational Period {currentPeriod?.periodNumber}{' '}
              </div>
              <div className="mb-3">
                Started:{' '}
                {dayjs(currentPeriod?.startDate).format('MM/DD/YYYY HH:mm')}
              </div>
              Anticipated End:{' '}
              {currentPeriod?.currentPeriod?.anticipated_end_date_time
                ? dayjs(
                    currentPeriod?.currentPeriod?.anticipated_end_date_time
                  ).format('MM/DD/YYYY HH:mm')
                : 'Unknown'}{' '}
            </div>
            <div className="mt-3">
              <label className="form-label">Incident Type</label>
              <StylishNewSelect
                options={
                  IncidentTypes &&
                  IncidentTypes.map((i) => ({ label: i, value: i }))
                }
                value={incidentType}
                placeholder="Select a Type"
                onChange={(e) => incidentTypeHandler(e)}
                isClearable={false}
                isSearchable={false}
                isMulti={true}
                isDisabled={
                  !reduxValidateRBACPermissionForActionResult['incident:create']
                }
              />
            </div>
          </>
        )}
        {!!currentIncident && (
          <div className="button-group flex-wrap justify-content-end mt-4">
            <StylishNewButton
              type="button"
              className="button--secondary"
              onClick={() => setIsEditIncidentOpen(true)}
            >
              Edit
            </StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--tertiary button--reverse"
              onClick={() => handleArchiveClick()}
            >
              Archive
            </StylishNewButton>
          </div>
        )}
      </div>

      {isEditIncidentOpen && (
        <EditIncidentDialog
          show={isEditIncidentOpen}
          incident={currentIncident}
          onClose={() => setIsEditIncidentOpen(false)}
        />
      )}

      {showConfirmDialog && (
        <StylishConfirmDialog
          show={showConfirmDialog}
          dialogTitle={'Archive Confirmation'}
          dialogContent={
            <>
              {incidents.filter(
                (incident) =>
                  incident.id !== currentIncident.id && incident.archived !== 1
              ).length === 0 ? (
                <div>
                  This is your only unarchived incident. Please create a new one
                  before archiving this.
                </div>
              ) : (
                <>
                  <div className="p-1">
                    Are you sure you want to archive this incident?
                  </div>
                  <div className="p-1">
                    Please select a workspace to work in
                  </div>
                  <StylishNewSelect
                    options={incidents
                      .filter(
                        (incident) =>
                          incident.id !== currentIncident.id &&
                          incident.archived !== 1
                      )
                      .map((incident) => ({
                        label: incident.name,
                        value: incident.id,
                      }))}
                    placeholder="Select a workspace"
                    isSearchable={true}
                    onChange={(e) =>
                      setSelectedWorkspace(
                        incidents.find((incident) => incident.id === e.value)
                      )
                    }
                  />
                </>
              )}
            </>
          }
          disabled={!selectedWorkspace}
          dialogButtonText={'Archive'}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={archiveIncident}
        />
      )}
    </>
  );
}
